import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './BlogSection.css';

const BlogSection = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');

  const blogPosts = [
    {
      id: 1,
      title: "Building Scalable Microservices with Spring Boot",
      description: "Explore the best practices for designing and implementing microservices architecture using Spring Boot, including service discovery, load balancing, and fault tolerance patterns.",
      category: "Backend",
      readTime: "8 min read",
      publishDate: "2024-12-15",
      mediumUrl: "",
      tags: ["Spring Boot", "Microservices", "Java", "Architecture"],
      image: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=400&h=250&fit=crop"
    },
    {
      id: 2,
      title: "React Performance Optimization Techniques",
      description: "Deep dive into advanced React optimization strategies including memoization, lazy loading, code splitting, and virtual DOM optimization for building high-performance web applications.",
      category: "Frontend",
      readTime: "10 min read",
      publishDate: "2024-11-28",
      mediumUrl: "",
      tags: ["React", "Performance", "JavaScript", "Optimization"],
      image: "https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=400&h=250&fit=crop"
    },
    {
      id: 3,
      title: "AWS Lambda and Serverless Architecture Best Practices",
      description: "Learn how to design and deploy serverless applications using AWS Lambda, API Gateway, and other AWS services for cost-effective and scalable solutions.",
      category: "Cloud",
      readTime: "12 min read",
      publishDate: "2024-11-10",
      mediumUrl: "",
      tags: ["AWS", "Serverless", "Lambda", "Cloud Architecture"],
      image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=400&h=250&fit=crop"
    },
    {
      id: 4,
      title: "Database Design Patterns for High-Traffic Applications",
      description: "Comprehensive guide to database optimization techniques, indexing strategies, and scaling patterns for handling millions of requests efficiently.",
      category: "Database",
      readTime: "15 min read",
      publishDate: "2024-10-22",
      mediumUrl: "",
      tags: ["Database", "SQL", "Performance", "Scaling"],
      image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=400&h=250&fit=crop"
    },
    {
      id: 5,
      title: "Implementing CI/CD Pipelines with GitLab and Docker",
      description: "Step-by-step guide to setting up robust CI/CD pipelines using GitLab CI, Docker containers, and automated testing for streamlined development workflows.",
      category: "DevOps",
      readTime: "11 min read",
      publishDate: "2024-10-05",
      mediumUrl: "",
      tags: ["GitLab", "Docker", "CI/CD", "DevOps"],
      image: "https://images.unsplash.com/photo-1618401471353-b98afee0b2eb?w=400&h=250&fit=crop"
    },
    {
      id: 6,
      title: "Modern JavaScript ES6+ Features Every Developer Should Know",
      description: "Explore the latest JavaScript features including async/await, destructuring, modules, and functional programming concepts that improve code quality.",
      category: "Frontend",
      readTime: "9 min read",
      publishDate: "2024-09-18",
      mediumUrl: "",
      tags: ["JavaScript", "ES6+", "Modern JS", "Best Practices"],
      image: "https://images.unsplash.com/photo-1627398242454-45a1465c2479?w=400&h=250&fit=crop"
    },
    {
      id: 7,
      title: "API Security Best Practices and JWT Implementation",
      description: "Complete guide to securing REST APIs with JWT tokens, OAuth, rate limiting, and other security measures to protect your applications.",
      category: "Security",
      readTime: "13 min read",
      publishDate: "2024-09-01",
      mediumUrl: "",
      tags: ["API Security", "JWT", "OAuth", "Authentication"],
      image: "https://images.unsplash.com/photo-1563986768609-322da13575f3?w=400&h=250&fit=crop"
    },
    {
      id: 8,
      title: "Kafka Event Streaming in Distributed Systems",
      description: "Learn how to implement event-driven architecture using Apache Kafka for real-time data processing and communication between microservices.",
      category: "Backend",
      readTime: "14 min read",
      publishDate: "2024-08-15",
      mediumUrl: "",
      tags: ["Kafka", "Event Streaming", "Distributed Systems", "Real-time"],
      image: "https://images.unsplash.com/photo-1518186285589-2f7649de83e0?w=400&h=250&fit=crop"
    },
    {
      id: 9,
      title: "Container Orchestration with Kubernetes",
      description: "Master Kubernetes fundamentals including pods, services, deployments, and scaling strategies for managing containerized applications in production.",
      category: "DevOps",
      readTime: "16 min read",
      publishDate: "2024-07-28",
      mediumUrl: "",
      tags: ["Kubernetes", "Containers", "Orchestration", "Production"],
      image: "https://images.unsplash.com/photo-1667372393119-3d4c48d07fc9?w=400&h=250&fit=crop"
    },
    {
      id: 10,
      title: "Full-Stack Testing Strategies: From Unit to E2E",
      description: "Comprehensive testing approach covering unit tests, integration tests, and end-to-end testing using Jest, Selenium, and modern testing frameworks.",
      category: "Testing",
      readTime: "12 min read",
      publishDate: "2024-07-10",
      mediumUrl: "",
      tags: ["Testing", "Jest", "Selenium", "E2E Testing"],
      image: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&h=250&fit=crop"
    }
  ];

  const categories = ['All', 'Frontend', 'Backend', 'Cloud', 'DevOps', 'Database', 'Security', 'Testing'];

  const filteredPosts = selectedCategory === 'All' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <section className="blog-section" id="blog">
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2>Blog & Articles</h2>
          <p>Technical insights and experiences from my development journey</p>
        </motion.div>

        <motion.div
          className="category-filters"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {categories.map((category) => (
            <button
              key={category}
              className={`filter-btn ${selectedCategory === category ? 'active' : ''}`}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </button>
          ))}
        </motion.div>

        {/* <motion.div
          className="blog-stats"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <div className="stat-item">
            <i className="fas fa-newspaper"></i>
            <span className="stat-number">{blogPosts.length}</span>
            <span className="stat-label">Articles Published</span>
          </div>
          <div className="stat-item">
            <i className="fab fa-medium"></i>
            <span className="stat-number">5K+</span>
            <span className="stat-label">Medium Reads</span>
          </div>
          <div className="stat-item">
            <i className="fas fa-heart"></i>
            <span className="stat-number">500+</span>
            <span className="stat-label">Article Likes</span>
          </div>
        </motion.div> */}

        <AnimatePresence mode="wait">
          <motion.div
            key={selectedCategory}
            className="blog-grid"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
          >
            {filteredPosts.map((post, index) => (
              <motion.article
                key={post.id}
                className="blog-card"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
              >
                <div className="blog-image">
                  <img src={post.image} alt={post.title} />
                  <div className="category-badge">{post.category}</div>
                </div>
                
                <div className="blog-content">
                  <div className="blog-meta">
                    <span className="publish-date">
                      <i className="fas fa-calendar"></i>
                      {formatDate(post.publishDate)}
                    </span>
                    <span className="read-time">
                      <i className="fas fa-clock"></i>
                      {post.readTime}
                    </span>
                  </div>
                  
                  <h3 className="blog-title">{post.title}</h3>
                  <p className="blog-description">{post.description}</p>
                  
                  <div className="blog-tags">
                    {post.tags.map((tag, tagIndex) => (
                      <span key={tagIndex} className="tag">{tag}</span>
                    ))}
                  </div>
                  
                  <a 
                    href={post.mediumUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="read-more-btn"
                  >
                    <span>Read on Medium</span>
                    <i className="fas fa-external-link-alt"></i>
                  </a>
                </div>
              </motion.article>
            ))}
          </motion.div>
        </AnimatePresence>

        <motion.div
          className="blog-cta"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <h3>Want to read more?</h3>
          <p>Follow me on Medium for the latest articles and insights</p>
          <a 
            href="" 
            target="_blank" 
            rel="noopener noreferrer"
            className="medium-follow-btn"
          >
            <i className="fab fa-medium"></i>
            Follow on Medium
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default BlogSection;
